import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useTemplate } from '../../../../../features/template/TemplateProvider';
import useApi from '../../../../../hook/useApi';
import { ButtonCancel, ButtonPrimary } from '../../../../../utils/Button';
import cn from '../../../../../utils/cn';
import { GetCurrencySymbol } from '../../../../../utils/Currency';
import {
    addOption,
    InputDateField,
    SelectOption,
    TextareaField
} from '../../../../../utils/InputField';
import { GetFloatNumber } from '../../../../../utils/NumberFormatter';
import { ImageUploadingIcon } from '../../../../../utils/SVGIcon';
import { ApiGetPurchaseServicesList } from '../../../../Settings/Services/ServiceOperation';
import { useInvoiceInfo } from '../InvoiceInfoProvider';

const billingCycles = [
    { key: 1, value: 'monthly', text: 'Monthly' },
    { key: 3, value: 'quarterly', text: 'Quarterly' },
    { key: 6, value: 'half-yearly', text: 'Half-Yearly' },
    { key: 12, value: 'yearly', text: 'Yearly' }
];

function PackageItems({ arrayList, handlePackageSelect, handleCustomInput, checkedItems }) {
    if (arrayList?.length === 0) {
        return (
            <div className="h-full flex justify-center items-center py-4 bg-secondary-100 rounded-10">
                <span className="text-gray-500">
                    No Package found for this service or user current currency!
                </span>
            </div>
        );
    }
    return (
        <ul className="w-full bg-secondary-50 rounded-10 divide-y">
            {arrayList?.map((option) => {
                return (
                    <li key={option.id} className="w-full flex flex-col p-3">
                        <span className="text-[18px] font-medium line-clamp-1">
                            {option?.serviceTitle}
                            {/* <span className="text-sm font-normal ml-1">
                                Duration: {option?.isDurationBased ? 'Yearly' : 'Fixed'}
                            </span> */}
                        </span>
                        <div className="ml-3 flex flex-col gap-2">
                            {option?.packages?.map((el) => {
                                return (
                                    <label
                                        htmlFor={el?.pricingId}
                                        key={el?.pricingId}
                                        className="size-full flex items-center justify-start gap-2  px-3 "
                                    >
                                        <input
                                            type="checkbox"
                                            id={el?.pricingId}
                                            onChange={(e) => handlePackageSelect(e, el, option)}
                                        />
                                        <div className="flex justify-between items-center w-full text-base font-normal">
                                            <p className="flex gap-1 items-center">
                                                <span>{el?.packageName}</span>
                                                <span className="text-sm font-normal">
                                                    ({el?.unitName})
                                                </span>
                                            </p>
                                            {el?.amount === -101 ? (
                                                <span>
                                                    {GetCurrencySymbol(el?.currency)}{' '}
                                                    <input
                                                        type="text"
                                                        onChange={(e) => handleCustomInput(e, el)}
                                                        ref={(input) => {
                                                            if (input) {
                                                                if (
                                                                    !checkedItems.includes(
                                                                        el.packageId
                                                                    )
                                                                ) {
                                                                    // eslint-disable-next-line no-param-reassign
                                                                    input.value = '';
                                                                }
                                                            }
                                                        }}
                                                        placeholder="Enter Amount"
                                                        className="w-30 h-8 p-1 rounded-5 border border-gray-300 focus:outline-none disabled:bg-primary-100"
                                                        disabled={
                                                            !checkedItems.includes(el.packageId)
                                                        }
                                                    />
                                                </span>
                                            ) : (
                                                <span>
                                                    {el?.amount === -102 ? (
                                                        <span>Free</span>
                                                    ) : (
                                                        <span>
                                                            {GetCurrencySymbol(el?.currency)}
                                                            {GetFloatNumber(el?.amount)}
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}

function AddNewServiceModal() {
    const { setMessage, closeModal, modalData } = useTemplate();
    const { userId, currency } = modalData || {};
    const [checkedItems, setCheckedItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [btnLoading, setBtnLoading] = useState(false);
    const [servicesList, setServicesList] = useState([]);
    const [formData, setFormData] = useState([]);
    const { post } = useApi();
    const { handleReload } = useInvoiceInfo();

    const [formData2, setFormData2] = useState({
        ServiceStartDate: dayjs().format('YYYY-MM-DD'), // output - 2023-10-10
        BillingCycle: 'yearly',
        Comment: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name) {
            setFormData2((prev) => ({ ...prev, [name]: value }));
        }
    };

    // Fetch services list
    useEffect(() => {
        setIsLoading(true);
        // Fetch services list
        ApiGetPurchaseServicesList(userId)
            .then(({ data: resData }) => {
                setServicesList(resData.services);
                setIsLoading(false);
            })
            .catch((err) => {
                setIsLoading(false);
                setMessage({ type: 'error', text: err.toString() });
            });
    }, [userId]);
    // select package item
    const handlePackageSelect = (e, packageItem, serviceItem) => {
        setCheckedItems((prev) => {
            if (prev.includes(packageItem.packageId)) {
                return prev.filter((el) => el !== packageItem.packageId);
            }
            return [...prev, packageItem.packageId];
        });
        const { checked } = e.target;
        if (checked) {
            if (packageItem?.amount === -101) {
                // if is already in the list then return
                if (formData.some((el) => el.AppServicePackageId === packageItem.packageId)) {
                    return null;
                }
                setFormData((prev) => [
                    ...prev,
                    {
                        AppServiceId: serviceItem.id,
                        AppServicePackageId: packageItem.packageId,
                        Amount: 0,
                        IsDurationBased: serviceItem.isDurationBased
                    }
                ]);
            } else if (packageItem?.amount === -102) {
                // free package
                // if is already in the list then return
                if (formData.some((el) => el.AppServicePackageId === packageItem.packageId)) {
                    return null;
                }
                setFormData((prev) => [
                    ...prev,
                    {
                        AppServiceId: serviceItem.id,
                        AppServicePackageId: packageItem.packageId,
                        Amount: 0,
                        IsDurationBased: serviceItem.isDurationBased
                    }
                ]);
            } else {
                setFormData((prev) => [
                    ...prev,
                    {
                        AppServiceId: serviceItem.id,
                        AppServicePackageId: packageItem.packageId,
                        Amount: packageItem?.amount || 0,
                        IsDurationBased: serviceItem.isDurationBased
                    }
                ]);
            }
        }
        if (!checked) {
            setFormData((prev) =>
                prev.filter((el) => el.AppServicePackageId !== packageItem.packageId)
            );
        }

        return null;
    };

    // modifying payload
    const payload = useMemo(() => {
        return {
            InvoiceId: modalData?.id,
            CustomerId: userId,
            PackageData: formData,
            Comment: formData2.Comment || '',
            BillingCycle: String(formData2.BillingCycle) || 'yearly',
            ServiceStartDate: formData2.ServiceStartDate
        };
    }, [formData, userId, modalData, formData2]);

    // handle custom input
    const handleCustomInput = (e, packageItem) => {
        const { value } = e.target;
        const index = formData.findIndex((el) => el.AppServicePackageId === packageItem.packageId);
        if (index !== -1) {
            setFormData((prev) => {
                // eslint-disable-next-line no-param-reassign
                prev[index].Amount = Number(value) || 0;
                return [...prev];
            });
        } else {
            setFormData((prev) => [
                ...prev,
                {
                    AppServiceId: packageItem.AppServiceId,
                    AppServicePackageId: packageItem.packageId,
                    Amount: Number(value) || 0,
                    IsDurationBased: packageItem.isDurationBased
                }
            ]);
        }
    };

    // submit form
    const handleSubmit = (e) => {
        e.preventDefault();
        setBtnLoading(true);
        post(`/api/purchase/service/create-invoice`, payload)
            .then(({ error, message }) => {
                if (error) {
                    throw new Error(message);
                }
                setMessage({ type: 'success', text: message });
                handleReload();
                closeModal();
            })
            .catch((error) => {
                setMessage({ type: 'error', text: error?.message });
            })
            .finally(() => {
                setBtnLoading(false);
            });
    };
    // select fixed item then disable billing cycle
    const isBillingCycleDisabled = formData.every((item) => {
        const service = servicesList.find((sr) =>
            sr.packages.some(
                (pkg) => pkg.packageId === item.AppServicePackageId && !sr.isDurationBased
            )
        );
        return service !== undefined;
    });

    return (
        <form onSubmit={handleSubmit} className={cn('flex flex-col gap-4 h-full', {})}>
            <div className="flex gap-6 h-100">
                <div className="flex flex-col gap-2 flex-[1_1_40%]">
                    <div>
                        <InputDateField
                            label="Service Start Date"
                            name="ServiceStartDate"
                            formData={formData2}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <SelectOption
                        label="Billing Cycle"
                        name="BillingCycle"
                        formData={formData2}
                        onChange={handleChange}
                        options={billingCycles.map((val) =>
                            addOption(val.key, val.text, val.value)
                        )}
                        required
                        // required if there is no service selected then disable inline condition
                        disabled={isBillingCycleDisabled}
                    />
                    <div className="text-sm text-error mt-3">
                        * The billing cycle is applicable only to yearly packages.
                    </div>
                    <TextareaField
                        formData={formData2}
                        name="Comment"
                        label="Reference"
                        placeholder="Type your comment here..."
                        onChange={handleChange}
                        optional
                    />
                </div>
                <div className={cn(`h-full flex flex-col flex-[1_1_60%]`)}>
                    <div className="h-[calc(100%_-_100px)] relative">
                        {isLoading && (
                            <div className="absolute size-40 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                                <ImageUploadingIcon />
                            </div>
                        )}
                        {isLoading && servicesList?.length === 0 ? (
                            <div className="h-full flex justify-center items-center py-4 bg-secondary-100 rounded-10">
                                <span className="text-gray-500">
                                    No service found or user current currency!
                                </span>
                            </div>
                        ) : (
                            <div className="h-full overflow-x-auto rounded-10 custom-scrollbar">
                                <PackageItems
                                    arrayList={servicesList}
                                    handlePackageSelect={handlePackageSelect}
                                    handleCustomInput={handleCustomInput}
                                    checkedItems={checkedItems}
                                />
                            </div>
                        )}
                    </div>
                    <table className="flex flex-col mt-2 flex-1 border *:leading-loose *:px-2 text-sm">
                        <tbody>
                            <tr className="w-full  flex justify-between border-b *:border-b-0 ">
                                <td className="border-r w-1/2">Recurring Amount</td>
                                <td className="w-1/2 text-right">
                                    {GetCurrencySymbol(currency)}
                                    {GetFloatNumber(
                                        formData.reduce((acc, curr) => {
                                            if (curr.IsDurationBased) {
                                                // now check billing cycle
                                                if (formData2.BillingCycle === 'yearly') {
                                                    return acc + curr.Amount;
                                                }
                                                if (formData2.BillingCycle === 'half-yearly') {
                                                    return acc + curr.Amount / 2;
                                                }
                                                if (formData2.BillingCycle === 'quarterly') {
                                                    return acc + curr.Amount / 4;
                                                }
                                                if (formData2.BillingCycle === 'monthly') {
                                                    return acc + curr.Amount / 12;
                                                }
                                                return acc;
                                            }
                                            return acc;
                                        }, 0)
                                    )}
                                </td>
                            </tr>
                            <tr className="w-full flex justify-between border-b">
                                <td className="border-r w-1/2">One-Time Amount</td>
                                <td className="w-1/2 text-right">
                                    {GetCurrencySymbol(currency)}
                                    {GetFloatNumber(
                                        formData.reduce(
                                            (acc, curr) =>
                                                curr.IsDurationBased ? acc : acc + curr.Amount,
                                            0
                                        )
                                    )}
                                </td>
                            </tr>
                            <tr className="w-full flex justify-between">
                                <td className="border-r w-1/2 font-medium">Total</td>
                                <td className="w-1/2 text-right font-medium">
                                    {GetCurrencySymbol(currency)}
                                    {GetFloatNumber(
                                        formData.reduce((acc, curr) => {
                                            let amount = curr.Amount;
                                            if (curr.IsDurationBased) {
                                                // now check billing cycle
                                                if (formData2.BillingCycle === 'yearly') {
                                                    amount = curr.Amount;
                                                }
                                                if (formData2.BillingCycle === 'half-yearly') {
                                                    amount = curr.Amount / 2;
                                                }
                                                if (formData2.BillingCycle === 'quarterly') {
                                                    amount = curr.Amount / 4;
                                                }
                                                if (formData2.BillingCycle === 'monthly') {
                                                    amount = curr.Amount / 12;
                                                }
                                            }
                                            return acc + amount;
                                        }, 0)
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="w-full flex justify-end items-center gap-2 mb-2">
                <ButtonCancel title="Cancel" onClick={() => closeModal()} />
                <ButtonPrimary title="Create Invoice" btnType="submit" loading={btnLoading} />
            </div>
        </form>
    );
}

export default AddNewServiceModal;
